import { Select, SelectProps } from 'antd';
import axios from 'axios';
import debounce from 'lodash/debounce';
import React, { useCallback, useState } from 'react';

export interface SearchOption {
  label: string;
  value: string | number;
}

interface SearchSelectProps extends Omit<SelectProps, 'options'> {
  fetchOptions: (search: string) => Promise<SearchOption[]>;
  debounceTimeout?: number;
  mode?: 'multiple' | 'tags';
  placeholder?: string;
}

export const SearchSelect: React.FC<SearchSelectProps> = ({
  fetchOptions,
  debounceTimeout = 500,
  mode = 'multiple',
  placeholder = 'Search...',
  ...props
}) => {
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedFetch = useCallback(
    debounce(async (searchText: string) => {
      if (!searchText) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const newOptions = await fetchOptions(searchText);
        setOptions(newOptions);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          console.error('Search failed:', error.message);
        }
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, debounceTimeout),
    [fetchOptions, debounceTimeout]
  );

  const handleSearch = (searchText: string) => {
    debouncedFetch(searchText);
  };

  const handleChange = (value: any, options: any) => {
    if (props.onChange) {
      props.onChange(value, options);
    }
  };

  return (
    <Select
      showSearch
      allowClear
      placeholder={placeholder}
      filterOption={false}
      onSearch={handleSearch}
      onChange={handleChange}
      notFoundContent={loading ? 'Loading...' : 'No offers found'}
      options={options}
      loading={loading}
      {...props}
    />
  );
};
